import { useState } from 'react';
import { shareURL } from '@telegram-apps/sdk-react';
import { TapUser } from '@/domain/types';
import { TELEGRAM_BOT_URL, getRandomShareText } from '@/domain/constants';
import { pushEvent } from '@/lib/gtm';

interface InviteScreenProps {
  user: TapUser | null;
}

export function InviteScreen({ user }: InviteScreenProps) {
  const [copied, setCopied] = useState(false);

  if (!user) return null;

  // Build referral links
  const referralCode = user.referralCode;
  const telegramLink = `${TELEGRAM_BOT_URL}?start=${referralCode}`;

  const handleShare = () => {
    const text = getRandomShareText();
    
    // Use SDK shareURL method (handles all the complexity correctly)
    if (shareURL.isAvailable()) {
      shareURL(telegramLink, text);
      pushEvent('referral_share', { method: 'telegram' });
    } else {
      // Fallback: copy to clipboard
      handleCopy();
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(telegramLink);
      setCopied(true);
      pushEvent('referral_share', { method: 'clipboard' });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = telegramLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      pushEvent('referral_share', { method: 'clipboard' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-auto p-5 pb-24">
      {/* Screen Title */}
      <h1 className="mb-6 text-2xl font-bold uppercase tracking-wider text-white">
        Invite & Earn
      </h1>

      {/* Bonus Card */}
      <div
        className="mb-6 flex items-center gap-4 rounded p-5"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 162, 255, 0.2), rgba(6, 182, 212, 0.2))',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(6, 182, 212, 0.4)',
        }}
      >
        <div className="flex h-12 w-12 items-center justify-center text-4xl">
          🎁
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
            Referral Bonus
          </span>
          <span className="text-3xl font-extrabold text-glow-cyan glow-cyan-sm">
            10%
          </span>
        </div>
      </div>

      {/* Referral Link */}
      <div className="mb-6">
        <label className="mb-2.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
          Your Referral Link
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={telegramLink}
            className="flex-1 overflow-hidden text-ellipsis rounded px-4 py-3.5 font-mono text-sm text-white"
            style={{
              background: 'rgba(23, 37, 84, 0.3)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
            }}
          />
          <button
            onClick={handleCopy}
            className="min-w-[90px] rounded px-6 py-3.5 text-sm font-semibold uppercase tracking-wide text-white transition-all active:scale-[0.98]"
            style={{
              background: copied
                ? 'rgba(34, 197, 94, 0.8)'
                : 'linear-gradient(135deg, #00a2ff, #06b6d4)',
              border: '1px solid rgba(6, 182, 212, 0.5)',
            }}
          >
            {copied ? '✓' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Rewards Tiers */}
      <div className="mb-6">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-glow-cyan">
          <span>💰</span> Referral Rewards
        </h2>
        <div className="flex flex-col gap-2">
          <RewardRow label="You both get on signup" value="+500 pts each" highlight />
          <RewardRow label="Friend reaches 1000 pts" value="+1000 pts" />
          <RewardRow label="10% of friend's points" value="ongoing" />
        </div>
      </div>

      {/* How It Works */}
      <div
        className="mb-6 rounded p-4"
        style={{
          background: 'rgba(23, 37, 84, 0.2)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(6, 182, 212, 0.2)',
        }}
      >
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-cyan-400">
          How it works
        </h3>
        <ol className="m-0 list-decimal space-y-2.5 pl-5 text-sm leading-relaxed text-gray-400">
          <li>Share your referral link with friends</li>
          <li>They join and start playing</li>
          <li>You earn 10% of their points forever!</li>
        </ol>
      </div>

      {/* Share Button */}
      <button
        onClick={handleShare}
        className="flex w-full items-center justify-center gap-2 rounded py-4 text-base font-semibold uppercase tracking-wide text-white transition-all active:scale-[0.98]"
        style={{
          background: 'linear-gradient(135deg, #00a2ff, #06b6d4)',
          border: '1px solid rgba(6, 182, 212, 0.5)',
          boxShadow: '0 0 20px rgba(0, 162, 255, 0.3)',
        }}
      >
        <span>📤</span> Share via Telegram
      </button>
    </div>
  );
}

interface RewardRowProps {
  label: string;
  value: string;
  highlight?: boolean;
}

function RewardRow({ label, value, highlight }: RewardRowProps) {
  return (
    <div
      className="flex items-center justify-between rounded p-3.5 transition-all"
      style={{
        background: highlight
          ? 'linear-gradient(135deg, rgba(0, 162, 255, 0.15), rgba(6, 182, 212, 0.15))'
          : 'rgba(23, 37, 84, 0.2)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: highlight
          ? '1px solid rgba(6, 182, 212, 0.4)'
          : '1px solid rgba(6, 182, 212, 0.2)',
      }}
    >
      <span className="text-sm text-gray-300">{label}</span>
      <span className="text-sm font-semibold text-cyan-400">{value}</span>
    </div>
  );
}
